import React, { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import LoadingState from "./LoadingState";
import ErrorState from "./ErrorState";
import RegistrationForm from "./RegistrationForm";
import SuccessState from "./SuccessState";
import { RedditProfile } from "../../types/reddit";
import { REDDIT_KARMA_ENDPOINT } from "../../config";
import { MIN_KARMA } from "./constants";

type FlowState = "input" | "checking" | "error" | "form" | "success";

// Matches ThreadSnoop's own username charset guard (a superset of Reddit's
// 3-20 char rule), so obviously-invalid input never reaches the network.
const USERNAME_PATTERN = /^[A-Za-z0-9_-]{3,64}$/;

class RedditLookupError extends Error {
  kind: "not_found" | "rate_limited" | "unavailable";
  constructor(kind: "not_found" | "rate_limited" | "unavailable", message: string) {
    super(message);
    this.kind = kind;
  }
}

interface ThreadSnoopUser {
  author: string;
  id: string;
  _meta?: {
    num_posts?: number;
    num_comments?: number;
    post_karma?: number;
    comment_karma?: number;
    total_karma?: number;
    earliest_post_at?: number;
    earliest_comment_at?: number;
  };
}

/**
 * Fetches a Reddit user's karma aggregate via our own same-origin proxy
 * (src/setupProxy.js in dev, netlify/functions/reddit-karma.js in prod) —
 * exactly ONE network call per verification attempt. The browser never
 * talks to ThreadSnoop directly: it has no CORS headers (it's a
 * server-side API), and the API key must never reach client code.
 */
async function fetchRedditUser(username: string): Promise<ThreadSnoopUser | null> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000);

  let response: Response;
  try {
    response = await fetch(
      `${REDDIT_KARMA_ENDPOINT}?username=${encodeURIComponent(username)}`,
      {
        method: "GET",
        headers: { Accept: "application/json" },
        signal: controller.signal,
      }
    );
  } catch (err) {
    throw new RedditLookupError(
      "unavailable",
      "Could not reach the Reddit verification service."
    );
  } finally {
    clearTimeout(timeoutId);
  }

  if (response.status === 429) {
    throw new RedditLookupError(
      "rate_limited",
      "Verification service is busy right now. Please try again in a moment."
    );
  }
  if (response.status === 401 || response.status === 402 || response.status >= 500) {
    throw new RedditLookupError(
      "unavailable",
      "Verification service is temporarily unavailable."
    );
  }
  if (!response.ok) {
    throw new RedditLookupError("unavailable", "Failed to fetch Reddit stats.");
  }

  const json = await response.json();
  return (json?.data?.user ?? null) as ThreadSnoopUser | null;
}

interface EligibilityCheckerProps {
  sectionId?: string;
}

export default function EligibilityChecker({ sectionId = "checker" }: EligibilityCheckerProps) {
  const [inputValue, setInputValue] = useState("");
  const [flowState, setFlowState] = useState<FlowState>("input");
  const [redditProfile, setRedditProfile] = useState<RedditProfile | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [isApiError, setIsApiError] = useState(false);

  // Guards against a double-click firing two lookups (and burning two
  // credits) before React re-renders into the "checking" state.
  const isCheckingRef = useRef(false);

  const cleanUsername = (val: string) => {
    let clean = val.trim();
    clean = clean.replace(/\/+$/, "");
    if (clean.includes("reddit.com")) {
      const match = clean.match(/reddit\.com\/u(?:ser)?\/([a-zA-Z0-9_-]+)/i);
      if (match && match[1]) {
        return match[1];
      }
    }
    clean = clean.replace(/^u\//i, "");
    return clean;
  };

  const handleCheck = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isCheckingRef.current) return;

    const username = cleanUsername(inputValue);
    if (!username) {
      setErrorMessage("Please enter your Reddit username or profile URL.");
      setIsApiError(false);
      setFlowState("error");
      return;
    }
    if (!USERNAME_PATTERN.test(username)) {
      setErrorMessage(
        "That doesn't look like a valid Reddit username. Usernames are 3-64 characters: letters, numbers, underscores, or hyphens only."
      );
      setIsApiError(false);
      setFlowState("error");
      return;
    }

    setErrorMessage("");
    setIsApiError(false);
    setFlowState("checking");
    isCheckingRef.current = true;

    try {
      // Exactly one API call for this whole verification attempt.
      const user = await fetchRedditUser(username);

      if (!user) {
        // Account not found is treated the same as "not eligible" — we
        // never reveal *why* to keep the messaging simple and consistent.
        setErrorMessage(`u/${username} is not eligible for the App20 Creator Program.`);
        setIsApiError(false);
        setFlowState("error");
        return;
      }

      const meta = user._meta || {};
      const karma =
        meta.total_karma ?? (meta.post_karma || 0) + (meta.comment_karma || 0);

      const earliestActivity = [meta.earliest_post_at, meta.earliest_comment_at]
        .filter((t): t is number => typeof t === "number" && t > 0)
        .sort((a, b) => a - b)[0];

      let accountAgeString = "Reddit member";
      if (earliestActivity) {
        const ageInMs = Date.now() - earliestActivity * 1000;
        const ageInDays = Math.max(1, Math.floor(ageInMs / (1000 * 60 * 60 * 24)));
        if (ageInDays >= 365) {
          const years = (ageInDays / 365).toFixed(1);
          accountAgeString = `Active since ${years} year${parseFloat(years) === 1 ? "" : "s"} ago`;
        } else if (ageInDays >= 30) {
          const months = Math.floor(ageInDays / 30);
          accountAgeString = `Active since ${months} month${months === 1 ? "" : "s"} ago`;
        } else {
          accountAgeString = `Active since ${ageInDays} day${ageInDays === 1 ? "" : "s"} ago`;
        }
      }

      const profile: RedditProfile = {
        username: user.author,
        karma,
        avatar: "",
        accountAgeString,
        createdUtc: earliestActivity || Date.now() / 1000,
        isEligible: karma >= MIN_KARMA,
        karmaVerified: true,
      };

      setRedditProfile(profile);

      if (profile.isEligible) {
        setFlowState("form");
      } else {
        // Never reveal the actual karma count — only eligibility status.
        setErrorMessage(`u/${profile.username} is not eligible for the App20 Creator Program.`);
        setIsApiError(false);
        setFlowState("error");
      }
    } catch (err) {
      const lookupError =
        err instanceof RedditLookupError
          ? err
          : new RedditLookupError("unavailable", "Could not connect to the Reddit verification service.");
      console.warn("Reddit lookup failed:", lookupError.kind, lookupError.message);
      setErrorMessage(lookupError.message);
      setIsApiError(true);
      setFlowState("error");
    } finally {
      isCheckingRef.current = false;
    }
  };

  const handleManualOverride = (manualKarma: number) => {
    const username = cleanUsername(inputValue) || "anonymous";
    const profile: RedditProfile = {
      username,
      karma: manualKarma,
      avatar: "",
      accountAgeString: "Unknown age (manual)",
      createdUtc: Date.now() / 1000,
      isEligible: true,
      karmaVerified: false,
    };
    setRedditProfile(profile);
    setFlowState("form");
  };

  const handleReset = () => {
    setFlowState("input");
    setRedditProfile(null);
    setErrorMessage("");
    setIsApiError(false);
  };

  return (
    <div id={sectionId} className="py-12 px-4 max-w-4xl mx-auto scroll-mt-20">
      <AnimatePresence mode="wait">
        {flowState === "input" && (
          <motion.div
            key="input-form"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.4 }}
            className="w-full max-w-2xl mx-auto bg-white border-2 border-indigo-100 rounded-3xl p-8 sm:p-10 text-slate-800 shadow-2xl shadow-indigo-100/50 relative overflow-hidden"
          >
            <div className="text-center mb-8">
              <span className="text-xs font-black uppercase tracking-widest text-[#3F51B5] bg-indigo-50 border border-indigo-100 px-4 py-1.5 rounded-full mb-4 inline-block">
                Check Eligibility
              </span>
              <h3 className="text-3xl font-black tracking-tight mb-3 text-[#3F51B5]">
                Are you eligible for ₹100 per post?
              </h3>
              <p className="text-slate-600 text-sm max-w-md mx-auto font-medium">
                Paste your Reddit profile link or username. We instantly check if you meet the{" "}
                {MIN_KARMA} Karma minimum.
              </p>
            </div>

            <form onSubmit={handleCheck} className="space-y-6">
              <div>
                <label className="block text-slate-600 text-xs font-bold uppercase tracking-wider mb-2.5">
                  Reddit Profile URL / Username
                </label>
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="reddit.com/user/yourusername or just yourusername"
                  className="w-full bg-slate-50 border-2 border-slate-200 px-4 py-4 rounded-2xl text-sm focus:outline-none focus:border-[#3F51B5] transition-all text-slate-900 placeholder-slate-400 font-semibold"
                />
              </div>

              <button
                type="submit"
                style={{
                  width: "100%",
                  padding: "14px 24px",
                  borderRadius: "25px",
                  fontFamily: "General-Sans-bold, sans-serif",
                  fontWeight: 800,
                  fontSize: "1.05rem",
                  background: "linear-gradient(90deg, #FFECB3 0%, #FFC107 100%)",
                  color: "#000",
                  border: "none",
                  cursor: "pointer",
                  boxShadow: "0 4px 15px rgba(255, 193, 7, 0.4)",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                }}
              >
                Verify My Account
                <ArrowRight className="w-5 h-5" />
              </button>
            </form>

            <div className="mt-8 pt-6 border-t border-slate-100 flex items-center justify-center gap-2 text-xs text-slate-500 font-medium">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
              <span>
                Minimum {MIN_KARMA} Combined Karma & positive account standing required.
              </span>
            </div>
          </motion.div>
        )}

        {flowState === "checking" && (
          <motion.div
            key="checking-animation"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <LoadingState username={cleanUsername(inputValue)} />
          </motion.div>
        )}

        {flowState === "error" && (
          <motion.div
            key="error-card"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <ErrorState
              username={cleanUsername(inputValue)}
              errorMessage={errorMessage}
              isApiError={isApiError}
              onReset={handleReset}
              onManualOverride={handleManualOverride}
            />
          </motion.div>
        )}

        {flowState === "form" && redditProfile && (
          <motion.div
            key="registration-form"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <RegistrationForm
              redditUsername={redditProfile.username}
              redditKarma={redditProfile.karma}
              karmaVerified={redditProfile.karmaVerified}
              onSubmitSuccess={() => setFlowState("success")}
            />
          </motion.div>
        )}

        {flowState === "success" && redditProfile && (
          <motion.div
            key="success-screen"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <SuccessState redditUsername={redditProfile.username} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
